import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { Firestore, doc, getDoc, updateDoc } from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import { User, UserInfo } from '../models/user.model';
import { NotificationOptions } from '../models/user.model';


@Injectable({
  providedIn: 'root'
})
export class UserService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private injector = inject(Injector);

  async getCurrentUserInfo(): Promise<UserInfo> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser?.uid) {
      throw new Error('No user is currently logged in');
    }

    try {
      const userDoc = await runInInjectionContext(this.injector, () =>
        getDoc(doc(this.firestore, 'Users', currentUser.uid))
      );

      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data() as User;
      return {
        id: currentUser.uid,
        email: userData.email || '',
        name: `${userData.name?.firstname || ''} ${userData.name?.lastname || ''}`.trim(),
        country: userData.country || '',
        affiliation: userData.affiliation || '',
        roles: userData.roles || [],
        affiliationAdmin: userData.affiliationAdmin || []
      };
    } catch (error) {
      console.error('Error fetching user info:', error);
      throw error;
    }
  }

  async updateNotificationSettings(settings: NotificationOptions): Promise<void> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser?.uid) {
      throw new Error('No user is currently logged in');
    }

    await runInInjectionContext(this.injector, () => {
      const userDocRef = doc(this.firestore, 'Users', currentUser.uid);
      return updateDoc(userDocRef, {
        notifications: settings
      });
    });
  }
  async getNotificationSettings(): Promise<NotificationOptions | undefined> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser?.uid) {
      throw new Error('No user is currently logged in');
    }

    const userDoc = await runInInjectionContext(this.injector, () =>
      getDoc(doc(this.firestore, 'Users', currentUser.uid))
    );

    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }
  
    const userData = userDoc.data() as User;
    return userData.notifications;
  }

  async canModifyAffiliation(affiliation: string): Promise<boolean> {
    try {
      const userInfo = await this.getCurrentUserInfo();
      
      // Super admin can modify all affiliations
      if (userInfo.roles?.includes('super-admin')) {
        return true;
      }

      // Org admin can only modify their assigned affiliations
      if (userInfo.roles?.includes('org-admin')) {
        return userInfo.affiliationAdmin?.includes(affiliation) || false;
      }

      // Regular users can only modify their own affiliation
      return userInfo.affiliation === affiliation;
    } catch (error) {
      console.error('Error checking affiliation permissions:', error);
      return false;
    }
  }

  async getFilteredAffiliations(): Promise<string[]> {
    try {
      const userInfo = await this.getCurrentUserInfo();

      // Super admin can see all affiliations
      if (userInfo.roles?.includes('super-admin')) {
        return [];  // Empty array means no filter
      }

      // Org admin can only see their assigned affiliations
      if (userInfo.roles?.includes('org-admin') && userInfo.affiliationAdmin?.length) {
        return userInfo.affiliationAdmin;
      }

      // Regular users can only see their own affiliation
      return [userInfo.affiliation];
    } catch (error) {
      console.error('Error getting filtered affiliations:', error);
      return [];
    }
  }
}
